import React, { useState, useRef } from 'react';
import {
   Box,
   Button,
   Typography,
   CircularProgress,
   Grid,
   Paper,
   IconButton,
   TextField,
   LinearProgress,
   Alert,
   InputAdornment,
   Chip,
} from '@mui/material';
import {
   CloudUpload as UploadIcon,
   Add as AddIcon,
   Close as CloseIcon,
   Photo as PhotoIcon,
   Image as CoverPhotoIcon,
   LocationOn as LocationIcon,
   CalendarToday as DateIcon,
} from '@mui/icons-material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import {
   collection,
   addDoc,
   updateDoc,
   serverTimestamp,
   doc,
   arrayUnion,
   Timestamp,
} from 'firebase/firestore';
import { db, storage } from '../../firebase/config';
import { useUser as useAuth } from '../../contexts/UserContext';
import GooglePlacesSearch from '../common/GooglePlacesSearch';
import { getImageTakenDate } from '../../utils/imageUtils';
import { preparePhotosForUpload } from '../../utils/preparePhotosForUpload';

interface PhotoUploadProps {
   tripId: string;
   onUploadComplete: () => void;
   tripLocation: {
      name: string;
      address?: string;
      coordinates: [number, number];
   } | null;
}

interface UploadingPhoto {
   file: File;
   preview: string;
   progress: number;
   title: string;
   description: string;
   error?: string;
   setAsCover?: boolean;
   location?: {
      name: string;
      address?: string;
      coordinates: {
         latitude: number;
         longitude: number;
      };
   };
   takenAt?: Date | null;
}

const extractExifData = (
   file: File
): Promise<{
   dateTaken?: Date | null;
   location?: {
      name: string;
      address?: string;
      coordinates: {
         latitude: number;
         longitude: number;
      };
   } | null;
}> => {
   return new Promise((resolve) => {
      // Default result with no EXIF data
      const result: {
         dateTaken?: Date | null;
         location?: {
            name: string;
            coordinates: {
               latitude: number;
               longitude: number;
            };
         } | null;
      } = {
         dateTaken: null,
         location: null,
      };

      // First, let's try to get the date from the file's lastModified property as a fallback
      if (file.lastModified) {
         result.dateTaken = new Date(file.lastModified);
      }

      // Create a FileReader to read the file
      const reader = new FileReader();

      reader.onload = function (e) {
         // This is a simplified implementation
         // In a real app with exif-js installed, you would use:
         // EXIF.getData(file, function() {
         //   const dateTaken = EXIF.getTag(this, "DateTimeOriginal");
         //   const latitude = EXIF.getTag(this, "GPSLatitude");
         //   const longitude = EXIF.getTag(this, "GPSLongitude");
         //   // Then process these values
         // });

         // For now, we'll just use our fallback date
         // and we'll attempt to extract basic metadata from the array buffer

         try {
            if (e.target?.result instanceof ArrayBuffer) {
               const view = new DataView(e.target.result);

               // Look for EXIF marker
               let offset = 2;
               while (offset < view.byteLength) {
                  if (view.getUint8(offset) === 0xff && view.getUint8(offset + 1) === 0xe1) {
                     // Found EXIF marker
                     // This is simplified; a full implementation would parse the EXIF data structure
                     // For now, we'll just rely on our fallback date from lastModified
                     break;
                  }
                  offset += 1;
               }
            }
         } catch (error) {}

         resolve(result);
      };

      // Read the first bytes of the file to look for EXIF data
      // In a real implementation with exif-js, you wouldn't need this
      reader.readAsArrayBuffer(file.slice(0, 64 * 1024));
   });
};

const TripPhotoUploader: React.FC<PhotoUploadProps> = ({
   tripId,
   onUploadComplete,
   tripLocation = null,
}) => {
   const { currentUser } = useAuth();
   const [uploadingPhotos, setUploadingPhotos] = useState<UploadingPhoto[]>([]);
   const [isUploading, setIsUploading] = useState(false);
   const [error, setError] = useState<string | null>(null);
   const [successMessage, setSuccessMessage] = useState<string | null>(null);
   const fileInputRef = useRef<HTMLInputElement>(null);

   const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
      if (!event.target.files || event.target.files.length === 0) return;

      const selectedFiles = Array.from(event.target.files);
      const newPhotos: UploadingPhoto[] = [...uploadingPhotos];

      // Check if we already have a cover photo in the existing uploaded photos
      const hasCoverPhoto = uploadingPhotos.some((photo) => photo.setAsCover);

      let filesProcessed = 0;
      const totalFiles = selectedFiles.length;

      selectedFiles.forEach((file) => {
         // Check if file is an image
         if (!file.type.startsWith('image/')) {
            setError(`${file.name} is not an image file`);
            return;
         }

         // Create a preview
         const reader = new FileReader();
         reader.onload = async (e) => {
            if (e.target && e.target.result) {
               const metadata = await getImageTakenDate(file);

               const newPhoto: UploadingPhoto = {
                  file,
                  preview: e.target.result as string,
                  progress: 0,
                  title: file.name.split('.')[0], // Default title from filename
                  description: '',
                  // Set as cover only if this is the first photo ever and no cover exists yet
                  setAsCover:
                     !hasCoverPhoto && newPhotos.length === 0 && uploadingPhotos.length === 0,
                  takenAt: metadata.takenDate
                     ? Timestamp.fromDate(metadata.takenDate).toDate()
                     : null,
                  location: {
                     name: tripLocation?.name || '',
                     address: tripLocation?.address || '',
                     coordinates: {
                        latitude: tripLocation?.coordinates[0] || 0,
                        longitude: tripLocation?.coordinates[1] || 0,
                     },
                  },
               };

               newPhotos.push(newPhoto);
               filesProcessed++;

               // Only update state when all files are processed
               if (filesProcessed === totalFiles) {
                  setUploadingPhotos([...newPhotos]);
               }

               // // Extract EXIF data
               // extractExifData(file).then((exifData) => {
               //    if (exifData.dateTaken) {
               //       newPhoto.takenAt = exifData.dateTaken;
               //    }

               //    if (
               //       exifData.location &&
               //       exifData.location.coordinates.latitude &&
               //       exifData.location.coordinates.longitude
               //    ) {
               //       // In a real app, you could reverse geocode these coordinates to get a location name
               //       newPhoto.location = exifData.location; // `${exifData.location.latitude.toFixed(6)}, ${exifData.location.longitude.toFixed(6)}`;
               //    }

               // });
            }
         };
         reader.readAsDataURL(file);
      });

      // Reset file input
      if (fileInputRef.current) {
         fileInputRef.current.value = '';
      }
   };

   const handleRemovePhoto = (index: number) => {
      const newPhotos = [...uploadingPhotos];

      // If removing a cover photo, assign another one if available
      if (newPhotos[index].setAsCover && newPhotos.length > 1) {
         const nextIndex = index === 0 ? 1 : 0;
         newPhotos[nextIndex].setAsCover = true;
      }

      newPhotos.splice(index, 1);
      setUploadingPhotos(newPhotos);
   };

   const handlePhotoTitleChange = (index: number, value: string) => {
      const newPhotos = [...uploadingPhotos];
      newPhotos[index].title = value;
      setUploadingPhotos(newPhotos);
   };

   const handlePhotoDescriptionChange = (index: number, value: string) => {
      const newPhotos = [...uploadingPhotos];
      newPhotos[index].description = value;
      setUploadingPhotos(newPhotos);
   };

   const handlePhotoLocationChange = (index: number, value: any) => {
      const newPhotos = [...uploadingPhotos];
      newPhotos[index].location = value;
      setUploadingPhotos(newPhotos);
   };

   const handlePhotoDateChange = (index: number, value: Date | null) => {
      const newPhotos = [...uploadingPhotos];
      newPhotos[index].takenAt = value;
      setUploadingPhotos(newPhotos);
   };

   const handleSetAsCover = (index: number) => {
      // Create a new copy of the photos array
      const newPhotos = [...uploadingPhotos];

      // First, unset all photos as cover
      newPhotos.forEach((photo) => {
         photo.setAsCover = false;
      });

      // Then set only the selected photo as cover
      newPhotos[index].setAsCover = true;

      // Apply the changes
      setUploadingPhotos(newPhotos);

      // Show feedback
      setSuccessMessage(`Set "${newPhotos[index].title}" as cover photo`);
      setTimeout(() => setSuccessMessage(null), 3000);
   };

   const uploadPhotos = async () => {
      if (!currentUser) {
         setError('You must be logged in to upload photos');
         return;
      }

      if (uploadingPhotos.length === 0) {
         setError('Please select at least one photo to upload');
         return;
      }

      setIsUploading(true);
      setError(null);
      setSuccessMessage(null);

      const { preparedByIndex, errors: prepareErrors } = await preparePhotosForUpload(
         uploadingPhotos.map((p) => p.file),
         { maxSizeBytes: 150 * 1024 * 1024 }
      );
      if (prepareErrors.length > 0) {
         setSuccessMessage(
            `${prepareErrors.length} file(s) could not be used: ${prepareErrors[0].error}${prepareErrors.length > 1 ? ' …' : ''}`
         );
      }
      const toUpload = uploadingPhotos
         .map((photo, i) => (preparedByIndex[i] ? { photo, file: preparedByIndex[i]! } : null))
         .filter((x): x is { photo: (typeof uploadingPhotos)[0]; file: File } => x != null);
      if (toUpload.length === 0) {
         setError('No photos could be prepared for upload. Please try different files.');
         setIsUploading(false);
         return;
      }

      let uploadedCount = 0;
      let failedCount = uploadingPhotos.length - toUpload.length;
      const totalCount = uploadingPhotos.length;
      const successfulUploads: any[] = [];
      let coverPhotoUrl: string | null = null;

      for (let idx = 0; idx < toUpload.length; idx++) {
         const { photo, file: preparedFile } = toUpload[idx];
         const i = uploadingPhotos.indexOf(photo);
         const metadata = await getImageTakenDate(preparedFile);

         try {
            const safeFileName = preparedFile.name.replace(/[^a-zA-Z0-9.]/g, '_');
            const fileName = `trips/${tripId}/photos/${Date.now()}_${safeFileName}`;
            const storageRef = ref(storage, fileName);

            const uploadTask = uploadBytesResumable(storageRef, preparedFile);

            // Return a promise that resolves when upload is complete
            await new Promise<void>((resolve, reject) => {
               uploadTask.on(
                  'state_changed',
                  (snapshot) => {
                     // Update progress
                     const progress = Math.round(
                        (snapshot.bytesTransferred / snapshot.totalBytes) * 100
                     );
                     const newPhotos = [...uploadingPhotos];
                     newPhotos[i].progress = progress;
                     setUploadingPhotos(newPhotos);
                  },
                  (error) => {
                     // Handle upload error

                     const newPhotos = [...uploadingPhotos];
                     newPhotos[i].error = `Upload failed: ${error.message}`;
                     setUploadingPhotos(newPhotos);
                     failedCount++;
                     resolve(); // Continue with next photo even if this one failed
                  },
                  async () => {
                     // Upload completed successfully
                     try {
                        // Get download URL
                        const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);

                        // Create a thumbnail URL (in a real app, you might generate an actual thumbnail)
                        const thumbnailUrl = downloadURL;

                        // If this photo is set as cover, save its URL
                        if (photo.setAsCover) {
                           coverPhotoUrl = downloadURL;
                        }

                        // Create photo document in Firestore
                        const photoData = {
                           url: downloadURL,
                           filePath: fileName,
                           thumbnailUrl: thumbnailUrl,
                           title: photo.title || 'Untitled Photo',
                           description: photo.description || '',
                           fileName: fileName,
                           size: preparedFile.size,
                           mimeType: preparedFile.type,
                           tripId: tripId,
                           uploadedBy: currentUser.uid,
                           uploadedAt: serverTimestamp(),
                           createdAt: photo.takenAt
                              ? Timestamp.fromDate(photo.takenAt)
                              : serverTimestamp(),
                           takenAt: photo.takenAt
                              ? Timestamp.fromDate(photo.takenAt)
                              : serverTimestamp(),
                           isCoverPhoto: photo.setAsCover || false,
                           // AI enhancement fields initialized to false
                           isEnhanced: false,
                           isTransformed: false,
                           userId: currentUser.uid,
                           tags: [],
                           likeCount: 0,
                           likedBy: [],
                           // Only include location if it exists and is not empty
                           ...(photo.location
                              ? {
                                   location: {
                                      ...photo.location,
                                   },
                                }
                              : {}), // Omit the location field entirely if it doesn't exist
                        };

                        // Double-check that URL is present before saving
                        if (!photoData.url) {
                           throw new Error('Cannot save photo without URL');
                        }

                        try {
                           // Create a photo document in the photos collection
                           const photoRef = await addDoc(collection(db, 'photos'), photoData);

                           // Use the ID from Firestore for consistency
                           const photoDataWithId = {
                              ...photoData,
                              id: photoRef.id,
                           };

                           successfulUploads.push(photoDataWithId);
                           uploadedCount++;
                        } catch (firestoreErr: any) {
                           const newPhotos = [...uploadingPhotos];
                           newPhotos[i].error = `Database error: ${firestoreErr.message}`;
                           setUploadingPhotos(newPhotos);
                           failedCount++;
                        }

                        resolve();
                     } catch (err) {
                        failedCount++;
                        reject(err);
                     }
                  }
               );
            });
         } catch (err: any) {
            failedCount++;
         }
      }

      // If a cover photo was uploaded, update the trip with the cover photo URL
      if (coverPhotoUrl) {
         try {
            const tripRef = doc(db, 'trips', tripId);
            await updateDoc(tripRef, {
               coverPhotoUrl: coverPhotoUrl,
               updatedAt: new Date(),
            });
         } catch (err) {}
      }

      setIsUploading(false);

      if (uploadedCount === 0) {
         setError('Failed to upload photos. Please try again.');
      } else if (failedCount > 0) {
         setSuccessMessage(
            `Successfully uploaded ${uploadedCount} out of ${totalCount} photos. ${failedCount} failed.`
         );
         // Only clear successful uploads
         setUploadingPhotos((prev) => prev.filter((photo) => photo.error));
      } else {
         setSuccessMessage(`Successfully uploaded ${uploadedCount} photos.`);
         setUploadingPhotos([]);
         onUploadComplete();
      }
   };

   const formatDate = (date: any) => {
      try {
         return new Date(date).toLocaleDateString('en-US', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
         });
      } catch (error) {
         return null;
      }
   };

   return (
      <Box>
         <Box sx={{ mb: 2 }}>
            <Typography variant='h6' gutterBottom>
               Upload Photos
            </Typography>
            <Typography variant='body2' color='text.secondary'>
               Select photos to add to your trip. The first photo will be used as the cover photo by
               default.
            </Typography>
         </Box>

         <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item>
               <input
                  type='file'
                  accept='image/*'
                  multiple
                  style={{ display: 'none' }}
                  ref={fileInputRef}
                  onChange={handleFileSelect}
               />
               <Button
                  variant='outlined'
                  startIcon={<AddIcon />}
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploading}
               >
                  Select Photos
               </Button>
            </Grid>
            <Grid item>
               <Button
                  variant='contained'
                  color='primary'
                  startIcon={
                     isUploading ? <CircularProgress size={20} color='inherit' /> : <UploadIcon />
                  }
                  onClick={uploadPhotos}
                  disabled={isUploading || uploadingPhotos.length === 0}
               >
                  {isUploading ? 'Uploading...' : 'Upload All Photos'}
               </Button>
            </Grid>
         </Grid>

         {error && (
            <Alert severity='error' sx={{ mb: 2 }}>
               {error}
            </Alert>
         )}

         {successMessage && (
            <Alert severity='success' sx={{ mb: 2 }}>
               {successMessage}
            </Alert>
         )}

         <Grid container spacing={2}>
            {uploadingPhotos.map((photo, index) => (
               <Grid item xs={12} sm={6} md={4} key={index}>
                  <Paper sx={{ p: 2, position: 'relative' }}>
                     {/* Image thumbnail with cover photo badge */}
                     <Box sx={{ position: 'relative', mb: 2 }}>
                        <Box
                           sx={{
                              height: 200,
                              width: '100%',
                              backgroundImage: `url(${photo.preview})`,
                              backgroundSize: 'cover',
                              backgroundPosition: 'center',
                              borderRadius: 1,
                              border: photo.setAsCover ? '3px solid #1976d2' : '1px solid #e0e0e0',
                              boxShadow: photo.setAsCover
                                 ? '0 0 10px rgba(25, 118, 210, 0.5)'
                                 : 'none',
                           }}
                        />

                        {/* Cover Photo badge and close button */}
                        <Box
                           sx={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              right: 0,
                              display: 'flex',
                              justifyContent: 'space-between',
                              p: 1,
                           }}
                        >
                           {photo.setAsCover && (
                              <Chip
                                 label='Cover Photo'
                                 color='primary'
                                 size='small'
                                 icon={<CoverPhotoIcon />}
                                 sx={{
                                    backgroundColor: 'rgba(25, 118, 210, 0.8)',
                                    fontWeight: 'bold',
                                    backdropFilter: 'blur(4px)',
                                 }}
                              />
                           )}

                           <IconButton
                              size='small'
                              onClick={() => handleRemovePhoto(index)}
                              disabled={isUploading}
                              sx={{
                                 backgroundColor: 'rgba(255, 255, 255, 0.8)',
                                 '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.9)' },
                              }}
                           >
                              <CloseIcon fontSize='small' />
                           </IconButton>
                        </Box>

                        {/* Cover photo button at the bottom of the image */}
                        <Button
                           variant={photo.setAsCover ? 'contained' : 'outlined'}
                           size='small'
                           startIcon={<CoverPhotoIcon />}
                           onClick={() => handleSetAsCover(index)}
                           disabled={isUploading}
                           fullWidth
                           sx={{
                              position: 'absolute',
                              bottom: 0,
                              left: 0,
                              right: 0,
                              borderTopLeftRadius: 0,
                              borderTopRightRadius: 0,
                              backgroundColor: photo.setAsCover
                                 ? 'rgba(25, 118, 210, 0.9)'
                                 : 'rgba(255, 255, 255, 0.8)',
                              color: photo.setAsCover ? 'white' : 'rgba(0, 0, 0, 0.7)',
                              fontWeight: photo.setAsCover ? 'bold' : 'normal',
                              backdropFilter: 'blur(4px)',
                              transition: 'all 0.2s ease-in-out',
                              '&:hover': {
                                 backgroundColor: photo.setAsCover
                                    ? 'rgba(25, 118, 210, 1)'
                                    : 'rgba(255, 255, 255, 0.9)',
                                 color: photo.setAsCover ? 'white' : 'rgba(0, 0, 0, 0.9)',
                              },
                           }}
                        >
                           {photo.setAsCover ? 'Cover Photo ✓' : 'Set as Cover'}
                        </Button>
                     </Box>
                     <TextField
                        fullWidth
                        label='Title'
                        variant='outlined'
                        size='small'
                        value={photo.title}
                        onChange={(e) => handlePhotoTitleChange(index, e.target.value)}
                        disabled={isUploading}
                        sx={{ mb: 2 }}
                     />
                     <TextField
                        fullWidth
                        label='Description'
                        variant='outlined'
                        size='small'
                        multiline
                        rows={2}
                        value={photo.description}
                        onChange={(e) => handlePhotoDescriptionChange(index, e.target.value)}
                        disabled={isUploading}
                        sx={{ mb: 2 }}
                     />

                     {/* <TextField
                       fullWidth
                       label='Location'
                       variant='outlined'
                       size='small'
                       value={photo.location || ''}
                       onChange={(e) => handlePhotoLocationChange(index, e.target.value)}
                       disabled={isUploading}
                       sx={{ mb: 2 }}
                       InputProps={{
                          startAdornment: (
                             <InputAdornment position='start'>
                                <LocationIcon fontSize='small' />
                             </InputAdornment>
                          ),
                       }}
                    /> */}
                     <GooglePlacesSearch
                        sx={{ mb: 2 }}
                        value={photo.location?.name || ''}
                        searchType='places'
                        label='Location'
                        onPlaceSelect={(place_name: any, lat: any, lng: any) => {
                           handlePhotoLocationChange(index, {
                              name: place_name,
                              coordinates: {
                                 latitude: lat,
                                 longitude: lng,
                              },
                           });
                        }}
                     />
                     <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <DateTimePicker
                           label='Date Taken'
                           value={formatDate(photo.takenAt || '')}
                           onChange={(date) => handlePhotoDateChange(index, date as Date | null)}
                           renderInput={(params) => (
                              <TextField
                                 {...params}
                                 fullWidth
                                 variant='outlined'
                                 size='small'
                                 sx={{ mb: 2 }}
                                 disabled={isUploading}
                                 InputProps={{
                                    ...params.InputProps,
                                    startAdornment: (
                                       <InputAdornment position='start'>
                                          <DateIcon fontSize='small' />
                                       </InputAdornment>
                                    ),
                                 }}
                              />
                           )}
                        />
                     </LocalizationProvider>
                     {isUploading && (
                        <>
                           <LinearProgress
                              variant='determinate'
                              value={photo.progress}
                              sx={{ mt: 1, height: 6, borderRadius: 3 }}
                           />
                           <Typography
                              variant='caption'
                              color='text.secondary'
                              sx={{ display: 'block', mt: 0.5, textAlign: 'center' }}
                           >
                              {photo.progress}% Uploaded
                           </Typography>
                        </>
                     )}
                     {photo.error && (
                        <Typography
                           color='error'
                           variant='caption'
                           sx={{ display: 'block', mt: 1 }}
                        >
                           {photo.error}
                        </Typography>
                     )}
                  </Paper>
               </Grid>
            ))}
         </Grid>
      </Box>
   );
};

export default TripPhotoUploader;
